import { Fragment, useCallback, useEffect, useRef, useState, VoidFunctionComponent } from 'react';
import { IconButton } from 'components/elements';
import {
  MenuButtonOptionList,
  MenuButtonOptionListItem,
  MenuButtonOptionListItemIcon,
  MenuButtonOptionListItemText,
  MenuButtonOptionListSeparator,
  StyledMenuButton,
} from './MenuButton.styled';
import { MenuButtonOption, MenuButtonProps } from './types';

export const MenuButton: VoidFunctionComponent<MenuButtonProps> = props => {
  const { icon = 'ellipsis vertical', options, variant = 'transparent', ...rest } = props;

  // Flag indicating if the dropdown menu is visible
  const [open, setOpen] = useState(false);

  // Reference to the dropdown menu used to check if a click occurs outside of it
  const listRef = useRef<HTMLDivElement>(null);

  /**
   * Handle clicks on the main button
   */
  const handleButtonClick = useCallback(() => {
    setOpen(!open);
  }, [open]);

  /**
   * Handle clicks on the document
   *
   * @param {MouseEvent} event Source click event object
   */
  const handleDocumentClick = useCallback(
    (event: MouseEvent) => {
      // Ignore clicks if the menu is not currently visible
      if (!open) {
        return;
      }

      // Check if the click occurred outside the dropdown menu and close it if so
      const isList = !!listRef.current?.contains(event.target as Node);
      if (!isList) {
        setOpen(false);
      }
    },
    [open],
  );

  /**
   * Render option item icon
   *
   * @param {MenuButtonOption} option Target option configuration
   */
  const renderOptionIcon = (option: MenuButtonOption) => {
    if (!option.icon) {
      return null;
    }

    return <MenuButtonOptionListItemIcon name={option.icon} style={{ color: option.iconColor }} />;
  };

  /**
   * Render option separator
   *
   * @param {MenuButtonOption} option Target option configuration
   * @param {number} index Option index
   */
  const renderOptionSeparator = (option: MenuButtonOption, index: number) => {
    if (!option.separator) {
      return null;
    }

    return <MenuButtonOptionListSeparator key={`separator:${index}`} />;
  };

  /**
   * Render option item
   *
   * @param {MenuButtonOption} option Target option configuration
   * @param {number} index Option index
   */
  const renderOption = (option: MenuButtonOption, index: number) => {
    // Crate option click handler
    const handleOptionClick = () => {
      if (option.disabled) {
        return;
      }

      // Close dropdown menu and trigger the click event
      setOpen(false);
      option.onClick && option.onClick();
    };

    return (
      <Fragment key={index}>
        {renderOptionSeparator(option, index)}
        <MenuButtonOptionListItem $disabled={option.disabled} onClick={handleOptionClick}>
          {renderOptionIcon(option)}
          <MenuButtonOptionListItemText>{option.text}</MenuButtonOptionListItemText>
        </MenuButtonOptionListItem>
      </Fragment>
    );
  };

  /**
   * Render option list when applicable
   */
  const renderOptionList = () => {
    if (!open) {
      return null;
    }

    return (
      <MenuButtonOptionList ref={listRef}>
        {options.filter(option => !option.hidden).map(renderOption)}
      </MenuButtonOptionList>
    );
  };

  // Listen for clicks outside of the opened dropdown menu
  useEffect(() => {
    document.addEventListener('click', handleDocumentClick);

    return () => document.removeEventListener('click', handleDocumentClick);
  }, [handleDocumentClick]);

  return (
    <StyledMenuButton>
      <IconButton
        {...rest}
        active={open}
        icon={icon}
        variant={variant}
        onClick={handleButtonClick}
      />
      {renderOptionList()}
    </StyledMenuButton>
  );
};
